import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import api from "../utils/api";

const CreateGameModal = ({ isOpen, onClose, onCreate, onUpdate, gameToEdit }) => {
  const [gameTitle, setGameTitle] = useState("");
  const [gameId, setGameId] = useState("");
  const [gameIcon, setGameIcon] = useState(null);
  const [platform, setPlatform] = useState("android");
  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Initialize form with game data when in edit mode
  useEffect(() => {
    if (gameToEdit) {
      setIsEditMode(true);
      setGameTitle(gameToEdit.gameTitle || "");
      setGameId(gameToEdit.gameId || "");
      setGameIcon(gameToEdit.gameIcon || null);
      setPlatform(gameToEdit.platform || "android");
    } else {
      setIsEditMode(false);
      resetForm();
    }
  }, [gameToEdit]);

  const resetForm = () => {
    setGameTitle("");
    setGameId("");
    setGameIcon(null);
    setPlatform("android");
  };

  const handleIconUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setGameIcon(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!gameTitle || !gameId) {
      toast.error("Please fill in all fields.");
      return;
    }

    setIsLoading(true);

    try {
      if (isEditMode) {
        const res = await api.put(`/api/game/update/${gameToEdit.gameId}`, {
          gameTitle,
          gameId,
          gameIcon: gameIcon || gameToEdit.gameIcon, // Keep existing icon if not changed
          platform,
        });
        
        if (res.status === 200) {
          toast.success("Game updated successfully!");
          onUpdate(res.data);
          onClose();
        }
      } else {
        const res = await api.post("/api/game/create", {
          gameTitle,
          gameId,
          gameIcon,
          platform,
        });

        if (res.status === 201) {
          toast.success("Game created successfully!");
          onCreate(res.data);
          onClose();
        }
      }
    } catch (error) {
      console.error("Error creating game:", error);
      const errorMessage = error.response?.data?.msg || "Failed to create game. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-96">
        <h2 className="text-xl font-semibold mb-4">{isEditMode ? 'Edit Game' : 'Create Game'}</h2>

        {/* Upload Icon */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Icon (Optional)</label>
          <div
            className="mt-1 flex items-center justify-center w-24 h-24 border-2 border-dashed rounded-lg cursor-pointer"
            onClick={() => document.getElementById("iconUpload").click()}
          >
            {gameIcon ? (
              <img src={gameIcon} alt="Game Icon" className="w-full h-full object-cover rounded-lg" />
            ) : (
              <span className="text-gray-400 text-2xl">+</span>
            )}
          </div>
          <input id="iconUpload" type="file" className="hidden" onChange={handleIconUpload} />
        </div>

        {/* Game Title Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Game Title</label>
          <input
            type="text"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Enter game title"
            value={gameTitle}
            onChange={(e) => setGameTitle(e.target.value)}
          />
        </div>

        {/* game ID / Package Name Input */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Game ID / Package Name</label>
          <input
            type="text"
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="com.example.game"
            value={gameId}
            onChange={(e) => setGameId(e.target.value)}
          />
        </div>

        {/* Platform Selection */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Platform</label>
          <div className="flex space-x-4 mt-2">
            <button
              className={`px-4 py-2 rounded-md ${
                platform === "android" ? "bg-green-600 text-white" : "bg-gray-200"
              }`}
              onClick={() => setPlatform("android")}
            >
              Android
            </button>
            <button
              className={`px-4 py-2 rounded-md ${
                platform === "ios" ? "bg-blue-600 text-white" : "bg-gray-200"
              }`}
              onClick={() => setPlatform("ios")}
            >
              iOS
            </button>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-end space-x-2">
          <button
            className="px-4 py-2 bg-gray-200 rounded-md text-gray-700 hover:bg-gray-300"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </button>

          <button
            className={`px-4 py-2 rounded-md flex items-center justify-center gap-2 transition-colors ${
              isLoading ? "bg-indigo-400 cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-700"
            } text-white`}
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? (
              <svg
                aria-hidden="true"
                className="w-5 h-5 text-gray-200 animate-spin fill-white"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 
                100.591C22.3858 100.591 0 78.2051 0 
                50.5908C0 22.9766 22.3858 0.59082 50 
                0.59082C77.6142 0.59082 100 22.9766 100 
                50.5908ZM9.08144 50.5908C9.08144 73.1895 
                27.4013 91.5094 50 91.5094C72.5987 
                91.5094 90.9186 73.1895 90.9186 
                50.5908C90.9186 27.9921 72.5987 9.67226 
                50 9.67226C27.4013 9.67226 9.08144 
                27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 
                97.8624 35.9116 97.0079 
                33.5539C95.2932 28.8227 92.871 
                24.3692 89.8167 20.348C85.8452 
                15.1192 80.8826 10.7238 75.2124 
                7.41289C69.5422 4.10194 63.2754 
                1.94025 56.7698 1.05124C51.7666 
                0.367541 46.6976 0.446843 41.7345 
                1.27873C39.2613 1.69328 37.813 
                4.19778 38.4501 6.62326C39.0873 
                9.04874 41.5694 10.4717 44.0505 
                10.1071C47.8511 9.54855 51.7191 
                9.52689 55.5402 10.0491C60.8642 
                10.7766 65.9928 12.5457 70.6331 
                15.2552C75.2735 17.9648 79.3347 
                21.5619 82.5849 25.841C84.9175 
                28.9121 86.7997 32.2913 88.1811 
                35.8758C89.083 38.2158 91.5421 
                39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
            ) : (
              isEditMode ? 'Update' : 'Create'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateGameModal;
